import { useCallback, useMemo, useState } from "react"
import { UseFormReturn } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { useIsFeatureEnabled } from "@/hooks/useIsFeatureEnabled"
import { useRunAiFeature } from "@/hooks/useRunAiFeature"
import { joinStrings } from "@/lib/joinString"
import { AiFeatureItem, AiInputButtons } from "@/modules/chats/components/chat-input/ai-input-buttons"
import { questionnaireSummarizerQuery } from "@/modules/chats/services/ai/questionnaireSummarizerQuery"
import { AnamnesisFormType } from "@/modules/patients/detail/progress-note/schema/anamnesis-schema"
import { callTranscriptSummarizerQuery } from "@/modules/patients/detail/progress-note/services/callTranscriptSummarizerQuery"
import { chatHistorySummarizerQuery } from "@/modules/patients/detail/progress-note/services/chatHistorySummarizerQuery"
import useCallCaseSummaryFeedbackStore from "@/modules/patients/detail/progress-note/stores/useCallCaseSummaryFeedbackStore"
import { useCountryParam } from "@/modules/patients/hooks/useCountryParam"
import { PatientContextAppointmentFragment$data } from "@/modules/patients/services/__generated__/PatientContextAppointmentFragment.graphql"
import { PatientFragment$data } from "@/modules/patients/services/__generated__/PatientFragment.graphql"

type AnamnesisAiRunningState = "DEFAULT" | "CHAT_SUMMARIZER" | "QUESTIONNAIRE_SUMMARIZER" | "CALL_TRANSCRIPT_SUMMARIZER"

type AnamnesisAiButtonsProps = {
  patient: PatientFragment$data
  appointmentContext: PatientContextAppointmentFragment$data
  progressNoteId: string
  questionnaireId: number | null | undefined
  hasCallTranscript: boolean
  form: UseFormReturn<AnamnesisFormType>
  /** Persist the anamnesis after an AI feature wrote into it (the section's force-save). */
  onAfterApply: () => void
}

export const AnamnesisAiButtons = ({
  patient,
  appointmentContext,
  progressNoteId,
  questionnaireId,
  hasCallTranscript,
  form,
  onAfterApply,
}: AnamnesisAiButtonsProps) => {
  const { t } = useTranslation()
  const country = useCountryParam()
  const isSttEnabled = useIsFeatureEnabled("SPEECH_TO_TEXT")
  const markCallCaseSummaryUsed = useCallCaseSummaryFeedbackStore((s) => s.markUsed)
  const runAiFeature = useRunAiFeature()

  const [aiRunning, setAiRunning] = useState<AnamnesisAiRunningState>("DEFAULT")

  const isCallChannel = appointmentContext.channel === "PHONE" || appointmentContext.channel === "VIDEO"

  const { getValues, setValue } = form

  const handleSummarizeChat = useCallback(() => {
    const oldAnamnesis = getValues("anamnesis") ?? ""
    return runAiFeature({
      context: "ANAMNESIS",
      start: () => setAiRunning("CHAT_SUMMARIZER"),
      stop: () => setAiRunning("DEFAULT"),
      faro: {
        event: "aiFeature_chat_history_summarizer_anamnesis",
        feature: "Summarize chat history for Anamnesis",
      },
      run: (aiFeatureId) =>
        chatHistorySummarizerQuery(country, {
          age: patient.age.years,
          appointmentId: appointmentContext.id,
          aiFeatureId,
          gender: patient.gender,
        }),
      selectMeta: (response) => response.summarizeChatAppointment,
      onSuccess: (meta) => {
        setValue("anamnesis", joinStrings([oldAnamnesis, meta.text], "\n"))
        onAfterApply()
      },
      onError: () => setValue("anamnesis", oldAnamnesis),
    })
  }, [appointmentContext, country, getValues, onAfterApply, patient, runAiFeature, setValue])

  const handleSummarizeQuestionnaire = useCallback(() => {
    const oldAnamnesis = getValues("anamnesis") ?? ""
    return runAiFeature({
      context: "ANAMNESIS",
      start: () => setAiRunning("QUESTIONNAIRE_SUMMARIZER"),
      stop: () => setAiRunning("DEFAULT"),
      faro: {
        event: "aiFeature_questionnaire_summarizer_anamnesis",
        feature: "Summarize questionnaire for Anamnesis",
      },
      run: (aiFeatureId) => {
        if (!questionnaireId) {
          return Promise.resolve(null)
        }
        return questionnaireSummarizerQuery(country, {
          flavour: "ANAMNESIS",
          aiRequestMetadata: {
            age: patient.age.years,
            appointmentId: appointmentContext.id,
            aiFeatureId,
            gender: patient.gender,
          },
        })
      },
      selectMeta: (response) => response.summarizeQuestionnaire,
      onSuccess: (meta) => {
        setValue("anamnesis", joinStrings([oldAnamnesis, meta.text], "\n"))
        onAfterApply()
      },
      onError: () => setValue("anamnesis", oldAnamnesis),
    })
  }, [appointmentContext, country, getValues, onAfterApply, patient, questionnaireId, runAiFeature, setValue])

  const handleSummarizeCallTranscript = useCallback(() => {
    const oldAnamnesis = getValues("anamnesis") ?? ""
    return runAiFeature({
      context: "ANAMNESIS",
      start: () => {
        setAiRunning("CALL_TRANSCRIPT_SUMMARIZER")
        markCallCaseSummaryUsed(appointmentContext.id)
      },
      stop: () => setAiRunning("DEFAULT"),
      faro: {
        event: "aiFeature_call_transcript_summarizer_anamnesis",
        feature: "Summarize call transcript for Anamnesis",
      },
      run: (aiFeatureId) =>
        callTranscriptSummarizerQuery(country, {
          age: patient.age.years,
          appointmentId: appointmentContext.id,
          progressNoteId,
          aiFeatureId,
          gender: patient.gender,
        }),
      selectMeta: (response) => response.summarizeCallTranscript,
      onSuccess: (meta) => {
        setValue("anamnesis", joinStrings([oldAnamnesis, meta.text], "\n\n"))
        onAfterApply()
      },
      onError: () => setValue("anamnesis", oldAnamnesis),
    })
  }, [
    appointmentContext,
    country,
    getValues,
    markCallCaseSummaryUsed,
    onAfterApply,
    patient,
    progressNoteId,
    runAiFeature,
    setValue,
  ])

  const aiFeatureItems = useMemo<AiFeatureItem[]>(
    () => [
      {
        type: "CHAT_SUMMARIZER",
        name: t("episodeOfCare.progressNote.anamnesis.aiFeature.chatSummarizer.name"),
        description: t("episodeOfCare.progressNote.anamnesis.aiFeature.chatSummarizer.description"),
        action: handleSummarizeChat,
        shouldShow: appointmentContext.channel === "CHAT",
      },
      {
        type: "QUESTIONNAIRE_SUMMARIZER",
        name: t("episodeOfCare.progressNote.anamnesis.aiFeature.questionnaireSummarizer.name"),
        description: t("episodeOfCare.progressNote.anamnesis.aiFeature.questionnaireSummarizer.description"),
        action: handleSummarizeQuestionnaire,
        shouldShow: !!questionnaireId && appointmentContext.channel !== "CHAT",
      },
      {
        type: "CALL_TRANSCRIPT_SUMMARIZER",
        name: t("episodeOfCare.progressNote.anamnesis.aiFeature.callTranscriptSummarizer.name"),
        description: t("episodeOfCare.progressNote.anamnesis.aiFeature.callTranscriptSummarizer.description"),
        action: handleSummarizeCallTranscript,
        shouldShow: isSttEnabled && isCallChannel && hasCallTranscript,
      },
    ],
    [
      appointmentContext.channel,
      handleSummarizeChat,
      handleSummarizeCallTranscript,
      handleSummarizeQuestionnaire,
      hasCallTranscript,
      isCallChannel,
      isSttEnabled,
      questionnaireId,
      t,
    ],
  )

  return (
    <AiInputButtons items={aiFeatureItems} isRunning={aiRunning !== "DEFAULT"} testId="anamnesis-ai-input-button" />
  )
}
